import { useState } from 'react';
import { useAssignTrial } from '../../api/funnel';
import { Modal } from '../../components/Modal';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../lib/api';
import { fromLocalInput } from '../../lib/format';
import type { AdminTrial } from '../../types/api';
import { TutorPicker } from '../../components/TutorPicker';
import { formatPreferredSlot, nextOccurrence } from './weekdays';

/**
 * Assigning a tutor to a paid trial.
 *
 * The parent's own windows are the first thing on screen and each one is a
 * button that fills in the time: the admin's job is to match a tutor to a
 * window the family already said works, and retyping it by hand is how that
 * goes wrong.
 *
 * Mounted with `key={trial.id}`, so every trial starts from a blank form
 * rather than inheriting the last one's tutor — a reset effect would have
 * to get that right on every new field added here, and this cannot.
 */
export function AssignTutorModal({
  trial,
  onClose,
}: {
  trial: AdminTrial;
  onClose: () => void;
}) {
  const assign = useAssignTrial();
  const toast = useToast();

  const [tutorId, setTutorId] = useState<string>();
  const [tutorName, setTutorName] = useState<string>();
  const [startLocal, setStartLocal] = useState(() =>
    trial.preferredSlots[0] ? nextOccurrence(trial.preferredSlots[0]) : '',
  );

  const startTime = fromLocalInput(startLocal);
  const canSubmit = Boolean(tutorId && startTime) && !assign.isPending;
  const parentZone = trial.preferredSlots[0]?.timezone ?? trial.student.timezone;

  const submit = () => {
    if (!tutorId || !startTime) return;
    assign.mutate(
      { id: trial.id, tutorId, startTime },
      {
        onSuccess: () => {
          toast.notify(
            `${tutorName ?? 'The tutor'} is booked for ${trial.student.fullName}'s trial.`,
            'success',
          );
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      width="lg"
      title="Assign a tutor"
      description={`${trial.student.fullName} · ${trial.subject.nameEn} · ${trial.bundle.nameEn}`}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSubmit}
            onClick={submit}
          >
            {assign.isPending && <Spinner />}
            Assign and book the lesson
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {trial.parentNote && (
          <div className="rounded-md border border-rule bg-surface-2 px-3 py-2">
            <p className="text-xs font-medium text-ink-2">What the parent told us</p>
            <p className="mt-1 text-sm text-ink">{trial.parentNote}</p>
          </div>
        )}

        <div>
          <p className="text-xs font-medium text-ink-2">
            Times the parent offered{' '}
            <span className="font-normal text-ink-3">({parentZone})</span>
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {trial.preferredSlots.map((slot) => (
              <button
                key={`${slot.weekday}-${slot.startLocal}`}
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setStartLocal(nextOccurrence(slot))}
              >
                {formatPreferredSlot(slot)}
              </button>
            ))}
          </div>
        </div>

        <TutorPicker
          value={tutorId}
          onChange={(id, name) => {
            setTutorId(id);
            setTutorName(name);
          }}
          subjectSlug={trial.subject.slug}
        />

        <label className="block">
          <span className="block text-xs font-medium text-ink-2">
            Lesson starts{' '}
            <span className="font-normal text-ink-3">
              (your timezone — the parent is in {parentZone})
            </span>
          </span>
          <input
            type="datetime-local"
            className="input mt-1"
            value={startLocal}
            onChange={(e) => setStartLocal(e.target.value)}
          />
        </label>

        {assign.isError && (
          <p role="alert" className="text-sm text-danger">
            {assign.error instanceof ApiError
              ? assign.error.message
              : 'Could not assign that tutor.'}
          </p>
        )}

        <p className="text-xs text-ink-3">
          This opens a slot in the tutor's calendar and books the trial lesson
          into it. If they are busy at that time the booking is refused rather
          than double-booked.
        </p>
      </div>
    </Modal>
  );
}
